import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { getKnowledge, loadKnowledge, saveKnowledge } from '../../../../services/feedback';
import FeedbackCompat from './FeedbackCompat';
import FeedbackKnowledgeLevels, { LEVELS } from './FeedbackKnowledgeLevels';
import { errorText } from './feedbackUi';

function KnowledgeEditor({ accountId, sku, onSaved, onClose }) {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [facts, setFacts] = useState('');
    const [faq, setFaq] = useState([]);
    const [proof, setProof] = useState([]);
    const [message, setMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        setData(null);
        getKnowledge({ request, accountId, sku })
            .then((value) => {
                if (cancelled) return;
                setData(value);
                setFacts(value.custom_facts || '');
                setFaq(value.faq || []);
                setProof(value.proof_links || []);
            })
            .catch((error) => !cancelled && setMessage(errorText(error)));
        return () => { cancelled = true; };
    }, [request, accountId, sku]);

    if (!data) return <div className="fb-card"><span className="fb-muted">{message || 'Загрузка…'}</span></div>;

    const updateFaq = (index, field, value) => setFaq(faq.map((pair, position) => (position === index ? { ...pair, [field]: value } : pair)));
    const save = async () => {
        try {
            const saved = await saveKnowledge({ request, accountId, sku, customFacts: facts, faq, proofLinks: proof.filter((link) => (link.url || '').trim()) });
            setData(saved);
            setMessage('Сохранено');
            onSaved(saved);
        } catch (error) {
            setMessage(errorText(error));
        }
    };

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-row fb-spread">
                <div>
                    <h4 style={{ margin: 0 }}>{data.name || 'Товар'}</h4>
                    <div className="fb-muted">Артикул {data.offer_id || '—'} · SKU {data.sku}</div>
                </div>
                <button type="button" onClick={onClose}>Закрыть</button>
            </div>
            <details>
                <summary className="fb-muted" style={{ cursor: 'pointer' }}>
                    Из карточки Ozon (подгружается автоматически){data.synced_facts ? `: ${data.synced_facts.split('\n').length} строк` : ''}
                </summary>
                <div className="fb-item__text" style={{ fontSize: 13, marginTop: 6 }}>
                    {data.synced_facts || <span className="fb-muted">Характеристики карточки ещё не загружены: синхронизируйте ассортимент и атрибуты в разделе «Ассортимент».</span>}
                </div>
            </details>
            <label>
                <div className="fb-muted" style={{ marginBottom: 4 }}>Что магазин знает о товаре (срок годности, состав, для кого, чем отличается, что нельзя обещать)</div>
                <textarea className="fb-textarea" rows={5} maxLength={4000} value={facts} onChange={(event) => setFacts(event.target.value)}
                    placeholder="Например: срок годности 3 года, подходит для сухой кожи, не содержит отдушек…" />
            </label>
            <div className="fb-faq">
                <div className="fb-muted">Частые вопросы и утверждённые ответы (ИИ будет опираться на них)</div>
                {faq.map((pair, index) => (
                    <div className="fb-faq__row" key={index}>
                        <input className="fb-input" placeholder="Вопрос" value={pair.q} onChange={(event) => updateFaq(index, 'q', event.target.value)} />
                        <input className="fb-input" placeholder="Ответ магазина" value={pair.a} onChange={(event) => updateFaq(index, 'a', event.target.value)} />
                        <button type="button" onClick={() => setFaq(faq.filter((_, position) => position !== index))}>Убрать</button>
                    </div>
                ))}
                <div><button type="button" onClick={() => setFaq([...faq, { q: '', a: '' }])}>Добавить вопрос</button></div>
            </div>
            <div className="fb-faq">
                <div className="fb-muted">Исходники, подтверждающие инфографику: ссылки на документы, сертификаты, скриншоты, таблицы. ИИ ссылается на них в ответах и в техподдержке.</div>
                {proof.map((link, index) => (
                    <div className="fb-faq__row" key={index}>
                        <input className="fb-input" placeholder="Ссылка" value={link.url || ''} onChange={(event) => setProof(proof.map((item, position) => (position === index ? { ...item, url: event.target.value } : item)))} />
                        <input className="fb-input" placeholder="Что это (например, сертификат соответствия)" value={link.note || ''} onChange={(event) => setProof(proof.map((item, position) => (position === index ? { ...item, note: event.target.value } : item)))} />
                        <button type="button" onClick={() => setProof(proof.filter((_, position) => position !== index))}>Убрать</button>
                    </div>
                ))}
                <div><button type="button" onClick={() => setProof([...proof, { url: '', note: '' }])}>Добавить ссылку</button></div>
            </div>
            <div className="fb-actions">
                <button type="button" className="primary" onClick={save}>Сохранить</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
        </div>
    );
}

function ProductsKnowledge({ accountId }) {
    const { request } = useApi();
    const [query, setQuery] = useState('');
    const [onlyFilled, setOnlyFilled] = useState(false);
    const [data, setData] = useState({ total: 0, filled: 0, items: [] });
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await loadKnowledge({ request, accountId, query: query.trim(), onlyFilled }));
        } catch (err) {
            setError(errorText(err));
        } finally {
            setLoading(false);
        }
    }, [request, accountId, query, onlyFilled]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="fb">
            <div className="fb-card">
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>База знаний о товарах</h4>
                        <div className="fb-muted">
                            Ассортимент подгружается из карточек кабинета (артикул, SKU, название). Добавьте то, чего нет в карточке:
                            чем точнее факты, тем реже ИИ отказывается отвечать. Заполнено {data.filled} из {data.total}.
                        </div>
                    </div>
                    <div className="fb-row">
                        <input className="fb-input" placeholder="Название, артикул или SKU" value={query} onChange={(event) => setQuery(event.target.value)} />
                        <label className="fb-note"><input type="checkbox" checked={onlyFilled} onChange={(event) => setOnlyFilled(event.target.checked)} /> Только заполненные</label>
                    </div>
                </div>
            </div>
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            <div className={selected ? 'fb-two' : ''}>
                <div className="fb-table-wrap">
                    <table className="fb-table">
                        <thead>
                            <tr><th>Артикул</th><th>SKU</th><th>Название</th><th className="fb-num">Остаток</th><th className="fb-num">Обращений</th><th>Знания</th></tr>
                        </thead>
                        <tbody>
                            {data.items.map((row) => (
                                <tr key={row.sku} className="fb-clickable" onClick={() => setSelected(row.sku)}>
                                    <td>{row.offer_id || '—'}</td>
                                    <td>{row.sku}</td>
                                    <td style={{ maxWidth: 360 }}>{row.name || '—'}</td>
                                    <td className="fb-num">{row.stock ?? '—'}</td>
                                    <td className="fb-num">{row.reviews || ''}</td>
                                    <td>{row.has_custom ? <span className="fb-chip fb-chip--good">{row.faq_count ? `есть, FAQ ${row.faq_count}` : 'есть'}</span> : <span className="fb-chip">нет</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && data.items.length === 0 && (
                        <div className="fb-empty">Товаров пока нет. Ассортимент появится после синхронизации карточек кабинета.</div>
                    )}
                </div>
                {selected && <KnowledgeEditor accountId={accountId} sku={selected} onSaved={reload} onClose={() => setSelected('')} />}
            </div>
        </div>
    );
}

export default function FeedbackKnowledgeTabs({ accountId }) {
    const [level, setLevel] = useState('products');
    const tabs = [{ id: 'products', label: 'Товары' }, ...LEVELS, { id: 'compat', label: 'Совместимости' }];
    return (
        <div className="fb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="account-tabs account-tabs--secondary">
                {tabs.map((item) => (
                    <button key={item.id} type="button" className={level === item.id ? 'active' : ''} onClick={() => setLevel(item.id)}>{item.label}</button>
                ))}
            </div>
            {level === 'products' && <ProductsKnowledge accountId={accountId} />}
            {level === 'compat' && <FeedbackCompat accountId={accountId} />}
            {level !== 'products' && level !== 'compat' && <FeedbackKnowledgeLevels accountId={accountId} level={level} />}
        </div>
    );
}
